import { BdsGrid } from 'blip-ds/dist/blip-ds-react/components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './styles.scss';

export type PaginationProps = {
    totalItems: number;
    itemsPerPage: number;
    itemsPerPageOptions: number[];
    onChange: (value: number) => void;
    onItemsPerPageChange: (value: number) => void;
};

const getBdsLanguage = (language?: string) => {
    const lang = (language || 'pt-BR').toLowerCase();
    if (lang.startsWith('en')) return 'en_US';
    if (lang.startsWith('es')) return 'es_MX';
    return 'pt_BR';
};

export const Pagination: React.FC<PaginationProps> = props => {
    const { i18n } = useTranslation();
    const paginationRef = useRef<any>(null);
    const { itemsPerPage, itemsPerPageOptions, onChange, onItemsPerPageChange } = props;
    // bds-pagination selects the first option initially, so the current page size must come first.
    const [orderedOptions] = useState<number[]>(() => [
        itemsPerPage,
        ...itemsPerPageOptions.filter(option => option !== itemsPerPage),
    ]);

    const handleChangePage = useCallback(
        (event: any) => {
            if (event.detail) {
                onChange?.(event.detail);
            }
        },
        [onChange],
    );

    const handleChangeItemsPerPage = useCallback(
        (event: any) => {
            const value = Number(event.detail);
            if (value && value !== itemsPerPage) {
                onItemsPerPageChange(value);
            }
        },
        [itemsPerPage, onItemsPerPageChange],
    );

    useEffect(() => {
        const { current } = paginationRef;
        if (current) {
            current.addEventListener('bdsPaginationChange', handleChangePage);

            return () => {
                current.removeEventListener('bdsPaginationChange', handleChangePage);
            };
        }
    }, [handleChangePage]);

    useEffect(() => {
        const { current } = paginationRef;
        if (current) {
            current.addEventListener('bdsItemsPerPageChange', handleChangeItemsPerPage);

            return () => {
                current.removeEventListener('bdsItemsPerPageChange', handleChangeItemsPerPage);
            };
        }
    }, [handleChangeItemsPerPage]);

    return (
        <BdsGrid xs="12" md="12" alignItems="center" gap="1" class="pagination">
            <bds-pagination
                ref={paginationRef}
                page-counter={true}
                items-page={JSON.stringify(orderedOptions)}
                number-items={props.totalItems}
                pages={Math.ceil(props.totalItems / itemsPerPage)}
                language={getBdsLanguage(i18n.resolvedLanguage ?? i18n.language)}
            />
        </BdsGrid>
    );
};
